import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const breakpoints = {
  mobile: '480px',
  tablet: '768px',
  laptop: '1024px',
  desktop: '1200px'
};

// Brand Colors - Nigerian/American theme
const brandColors = {
  nigerianGreen: '#008751',
  americanBlue: '#3C3B6E',
  sharedRed: '#B22234',
  sharedWhite: '#FFFFFF',
  mutedGreen: '#0a9d5f',
  mutedBlue: '#4a5fa8',
  mutedRed: '#c94456'
};

const FooterContainer = styled.footer`
  background: linear-gradient(180deg, #1a202c 0%, #2d3748 100%);
  color: #cbd5e0;
  padding: 3.5rem 2rem 2rem;
  width: 100%;
  margin-top: auto;
  margin-bottom: 0;
  border-top: 3px solid transparent;
  border-image: linear-gradient(
    to right, 
    ${brandColors.mutedGreen} 0%, 
    ${brandColors.mutedGreen} 33%, 
    ${brandColors.sharedWhite} 33%,
    ${brandColors.sharedWhite} 50%,
    ${brandColors.mutedBlue} 50%,
    ${brandColors.mutedBlue} 66%,
    ${brandColors.mutedRed} 66%,
    ${brandColors.mutedRed} 100%
  ) 1;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);

  @media (max-width: ${breakpoints.tablet}) {
    padding: 2.5rem 1.5rem 1.5rem;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 2rem 1rem 1rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.8fr 1.2fr 1fr 1fr;
  gap: 3rem;
  align-items: start;
  
  @media (max-width: ${breakpoints.laptop}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FooterTitle = styled.h3`
  color: #ffffff;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  opacity: 0.95;
`;

const CompanyName = styled.h2`
  color: #ffffff;
  font-size: 1.4rem;
  margin-bottom: 1rem;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.5px;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

const FooterText = styled.p`
  color: #cbd5e0;
  font-size: 0.9rem;
  line-height: 1.7;
  margin: 0;
  opacity: 0.9;
`;

const FooterLink = styled.a`
  color: #cbd5e0;
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  opacity: 0.9;
  font-weight: 500;
  
  &:hover {
    color: #ffffff;
    opacity: 1;
    transform: translateX(3px);
  }
`;

const BranchInfo = styled.div`
  margin-top: 0.5rem;
  padding: 1.1rem;
  background: rgba(10, 157, 95, 0.08);
  border-left: 3px solid ${brandColors.mutedGreen};
  border-radius: 6px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(10, 157, 95, 0.12);
    transform: translateX(2px);
  }
  
  &.coming-soon {
    background: rgba(74, 95, 168, 0.08);
    border-left-color: ${brandColors.mutedBlue};
    opacity: 0.75;
    
    &:hover {
      background: rgba(74, 95, 168, 0.12);
      opacity: 0.9;
    }
  }
`;

const BranchLabel = styled.span`
  color: #ffffff;
  font-weight: 700;
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
`;

const BranchDetails = styled.span`
  color: #e2e8f0;
  font-size: 0.85rem;
  line-height: 1.6;
  opacity: 0.9;
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(74, 85, 104, 0.5);
  
  @media (max-width: ${breakpoints.mobile}) {
    margin-top: 2rem;
    padding-top: 1.5rem;
  }
`;

const CopyrightText = styled.p`
  color: #a0aec0;
  font-size: 0.875rem;
  margin: 0;
  opacity: 0.85;
  
  &:first-child {
    font-weight: 600;
  }
`;

const SystemBadge = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, rgba(10, 157, 95, 0.15) 0%, rgba(74, 95, 168, 0.15) 100%);
  color: #68d391;
  padding: 0.45rem 1.1rem;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  margin-top: 0.75rem;
  border: 1px solid rgba(10, 157, 95, 0.35);
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const ContactItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.75rem;
`;

const ContactLabel = styled.span`
  color: #f7fafc;
  font-size: 0.75rem;
  font-weight: 700;
  opacity: 0.85;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const NoticeBox = styled.div`
  margin-top: 1rem;
  padding: 0.85rem;
  background: rgba(201, 68, 86, 0.12);
  border-left: 3px solid ${brandColors.mutedRed};
  border-radius: 4px;
  font-size: 0.85rem;
`;

const NoticeLabel = styled.strong`
  color: #fc8181;
  font-weight: 700;
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const NoticeText = styled.span`
  color: #e2e8f0;
  line-height: 1.5;
`;

const BrandFlag = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-top: 0.5rem;
`;

const FlagStripe = styled.div`
  width: 20px;
  height: 4px;
  border-radius: 2px;
  
  &.green {
    background-color: ${brandColors.mutedGreen};
  }
  
  &.white {
    background-color: rgba(255, 255, 255, 0.3);
  }
  
  &.blue {
    background-color: ${brandColors.mutedBlue};
  }
  
  &.red {
    background-color: ${brandColors.mutedRed};
  }
`;

const Footer = () => (
  <FooterContainer>
    <FooterContent>
      <FooterSection>
        <CompanyName>Nigeram Ventures & Investments Ltd</CompanyName>
        <FooterText>
          Professional accounting and inventory management system designed for enterprise-level operations and multi-branch coordination.
        </FooterText>
        <SystemBadge>Internal System</SystemBadge>
        <BrandFlag>
          <FlagStripe className="green" />
          <FlagStripe className="white" />
          <FlagStripe className="blue" />
          <FlagStripe className="red" />
        </BrandFlag>
      </FooterSection>
      
      <FooterSection>
        <FooterTitle>Our Branches</FooterTitle>
        <BranchInfo>
          <BranchLabel>Lagos (Main Branch)</BranchLabel>
          <BranchDetails>
            Bakery • Logistics • Photography/Videography
          </BranchDetails>
        </BranchInfo>
        <BranchInfo className="coming-soon">
          <BranchLabel>Ile-Ife</BranchLabel>
          <BranchDetails style={{ fontStyle: 'italic' }}>
            Coming Soon
          </BranchDetails>
        </BranchInfo>
      </FooterSection>
      
      <FooterSection>
        <FooterTitle>Support & Help</FooterTitle>
        <ContactItem>
          <ContactLabel>IT Support</ContactLabel>
          <FooterLink href="mailto:support@nigeram.com">
            support@nigeram.com
          </FooterLink>
        </ContactItem>
        <ContactItem>
          <ContactLabel>Helpdesk</ContactLabel>
          <FooterLink href="tel:+234XXXXXXXXXX">
            +234 XXX XXX XXXX
          </FooterLink>
        </ContactItem>
        <FooterText style={{ marginTop: '0.5rem', fontSize: '0.85rem', opacity: 0.8 }}>
          Business Hours:<br />
          Mon - Fri, 8:00 AM - 5:00 PM WAT
        </FooterText>
      </FooterSection>
      
      <FooterSection>
        <FooterTitle>Legal & Compliance</FooterTitle>
        <FooterLink as={Link} to="/terms">
          Terms of Use
        </FooterLink>
        <FooterLink as={Link} to="/privacy">
          Data Privacy Policy
        </FooterLink>
        <NoticeBox>
          <NoticeLabel>Notice:</NoticeLabel>
          <NoticeText>
            For internal use only. Unauthorized access is strictly prohibited.
          </NoticeText>
        </NoticeBox>
      </FooterSection>
    </FooterContent>
    
    <Copyright>
      <CopyrightText>
        © {new Date().getFullYear()} Nigeram Ventures & Investments Ltd. All rights reserved.
      </CopyrightText>
      <CopyrightText style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
        System Version 1.0 | Developed for Enterprise Operations
      </CopyrightText>
    </Copyright>
  </FooterContainer>
);

export default Footer;
